import { Button } from "@ui/Button";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { toast } from "@ui/molecules/Toasts/toast";
import { createResource, createSignal, onCleanup, onMount } from "solid-js";
import { headArtifact, toggleArtifactFavourite } from "@/client";

type AssetStateProps = {
	/**
	 * Id of the artifact this control represents (the `id` from the
	 * `artifact_created` chat event). Named `artifact_id` rather than
	 * `asset_id` because it identifies the artifact before it has been
	 * saved - "asset" is only what a saved artifact is called.
	 */
	artifact_id: string;
	/** Registry document id from hydrated `artifact_created` when autosaved. */
	document_id?: string;
	/** Registry favourite flag from hydrated `artifact_created` when present. */
	is_favourite?: boolean;
	/** Question the artifact belongs to; the save/unsave endpoints are scoped to it. */
	question_id: string;
};

/**
 * Fired after a save/unsave request has been confirmed against the asset
 * registry, so other views of the same question's saved assets (e.g. the
 * studio `ArtifactPanel`) know to refetch.
 */
export const ASSET_STATE_UPDATED_EVENT = "asset-state:updated";

export type AssetStateUpdatedEventDetail = {
	questionId: string;
	artifactId: string;
	saved: boolean;
};

/**
 * Save/saved toggle shown on chat response card toolbars. The saved state is
 * looked up from the asset registry (via a HEAD request) rather than tracked
 * locally - a 200 means the artifact has been saved.
 */
export const AssetState = (props: AssetStateProps) => {
	const registryDocumentId = () => props.document_id;

	const [isSaved, { refetch, mutate }] = createResource(
		() => ({
			artifactId: props.artifact_id,
			documentId: registryDocumentId(),
			isFavourite: props.is_favourite,
		}),
		async ({ artifactId, documentId, isFavourite }) => {
			if (typeof isFavourite === "boolean") {
				return isFavourite;
			}
			if (typeof documentId === "string" && documentId.length > 0) {
				return false;
			}
			const { response } = await headArtifact({
				path: { artifact_id: artifactId },
			});
			return response?.status === 200;
		},
	);
	const saved = () => isSaved() === true;
	const [isSubmitting, setIsSubmitting] = createSignal(false);

	/*
	 * An artifact can be shown twice at once - a document's card and the pane it
	 * opens in each carry a toolbar - and saving it from one is saving it from
	 * both. The announcement follows a HEAD request, so it can be taken as read.
	 */
	onMount(() => {
		const handleStateUpdated = (event: Event) => {
			const { artifactId, saved: nowSaved } = (
				event as CustomEvent<AssetStateUpdatedEventDetail>
			).detail;
			if (artifactId === props.artifact_id) {
				mutate(nowSaved);
			}
		};
		document.addEventListener(ASSET_STATE_UPDATED_EVENT, handleStateUpdated);
		onCleanup(() =>
			document.removeEventListener(
				ASSET_STATE_UPDATED_EVENT,
				handleStateUpdated,
			),
		);
	});

	const toggleSaved = async () => {
		if (isSubmitting()) {
			return;
		}
		setIsSubmitting(true);
		const wasSaved = saved();
		const documentId = registryDocumentId();
		if (!documentId) {
			toast.error({
				title: "This answer is not ready to save yet",
				store: false,
			});
			setIsSubmitting(false);
			return;
		}

		const res = await toggleArtifactFavourite({
			path: {
				document_id: documentId,
			},
		});

		if (res.error) {
			toast.error({
				title: wasSaved ? "Failed to unsave" : "Failed to save",
				store: false,
			});
			setIsSubmitting(false);
			return;
		}

		const toggledFavourite =
			typeof res.data?.is_favourite === "boolean"
				? res.data.is_favourite
				: await refetch();
		mutate(toggledFavourite);
		const nowSaved = toggledFavourite === true;
		document.dispatchEvent(
			new CustomEvent<AssetStateUpdatedEventDetail>(ASSET_STATE_UPDATED_EVENT, {
				detail: {
					questionId: props.question_id,
					artifactId: props.artifact_id,
					saved: nowSaved === true,
				},
			}),
		);
		setIsSubmitting(false);
	};

	return (
		<TooltipTrigger>
			<Button
				class="asset-state-button"
				monospaced
				variant="text"
				theme={saved() ? "success" : "monochrome"}
				symbol={saved() ? "check_circle" : "bookmark_border"}
				symbolFilled={saved()}
				symbolRelativeSize="even-larger"
				symbolPosition="after"
				disabled={isSubmitting()}
				aria-label={saved() ? "Remove from saved" : "Save answer"}
				data-artifact-id={props.artifact_id}
				onClick={() => void toggleSaved()}
			>
				{saved() ? "saved" : "save"}
			</Button>
			<Tooltip>
				{saved() ? "Remove from saved" : "Save this answer for later"}
			</Tooltip>
		</TooltipTrigger>
	);
};
